import { useEffect, useState, useCallback } from "react";
import { Check, X, Eye, Download, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHead, TableSkeleton, Pager, KpiCard } from "./ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Users, UserPlus, ShieldOff } from "lucide-react";

const PS = 20;
const Flag = ({ on }) => on ? <Check size={15} className="text-emerald-600" /> : <X size={15} className="text-rose-400" />;

export default function AdminUsers() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams({ page, page_size: PS });
    if (search) p.set("search", search);
    setData(null);
    api.get(`/admin/users?${p}`).then((r) => setData(r.data));
  }, [page, search]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => { setDetail({ loading: true }); const r = await api.get(`/admin/users/${id}`); setDetail(r.data); };
  const exportData = async (id) => {
    const r = await api.get(`/admin/users/${id}/export`);
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `user-${id}.json`; a.click();
    toast.success("Data export downloaded");
  };
  const anonymize = async (id) => {
    if (!window.confirm("Anonymize this user's personal data? This cannot be undone.")) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("User anonymized");
    setDetail(null); load();
  };

  return (
    <div data-testid="admin-users">
      <PageHead title="Users" subtitle="User management & consent status" />
      {data?.stats && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <KpiCard label="Total Users" value={data.stats.total} icon={Users} tint="#7209B7" />
          <KpiCard label="New This Month" value={data.stats.new_month} icon={UserPlus} tint="#2A9D8F" />
          <KpiCard label="Right-to-Erasure" value={data.stats.erasure} icon={ShieldOff} tint="#E76F51" />
        </div>
      )}
      <Input placeholder="Search name / email…" value={search} data-testid="user-search"
        onChange={(e) => { setPage(1); setSearch(e.target.value); }} className="max-w-xs mb-4" />

      <div className="rounded-2xl bg-white p-4 shadow-sm border border-black/5">
        {!data ? <TableSkeleton /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Registered</TableHead>
                <TableHead>Bookings</TableHead><TableHead>Cookies</TableHead><TableHead>Health</TableHead>
                <TableHead>Mktg</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="max-w-[180px] truncate">{u.email}</TableCell>
                    <TableCell className="text-xs">{(u.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell>{u.bookings}</TableCell>
                    <TableCell><Flag on={u.consent.cookies} /></TableCell>
                    <TableCell><Flag on={u.consent.health_data} /></TableCell>
                    <TableCell><Flag on={u.consent.marketing} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openDetail(u.id)} data-testid={`user-view-${u.id}`}><Eye size={15} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => exportData(u.id)} data-testid={`user-export-${u.id}`}><Download size={15} /></Button>
                      {!u.anonymized && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => anonymize(u.id)} data-testid={`user-anon-${u.id}`}><Trash2 size={15} /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager page={page} total={data.total} pageSize={PS} onPage={setPage} />
          </div>
        )}
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="user-detail-panel">
          <SheetHeader><SheetTitle>{detail?.name || "User"}</SheetTitle></SheetHeader>
          {detail && !detail.loading && (
            <div className="mt-4 space-y-4 text-sm">
              <div className="rounded-xl bg-gray-50 p-3">
                <div className="text-muted-foreground text-xs">{detail.email}</div>
                <div>{detail.country} · joined {(detail.created_at || "").slice(0, 10)}</div>
              </div>
              <div className="flex gap-4">
                <span className="flex items-center gap-1">Cookies <Flag on={detail.consent.cookies} /></span>
                <span className="flex items-center gap-1">Health <Flag on={detail.consent.health_data} /></span>
                <span className="flex items-center gap-1">Mktg <Flag on={detail.consent.marketing} /></span>
              </div>
              <div><div className="font-semibold text-[#111827] mb-1">Bookings ({detail.bookings.length})</div>
                {detail.bookings.slice(0, 6).map((b) => <div key={b.id} className="py-1 border-b border-black/5 flex justify-between"><span className="truncate">{b.provider_name}</span><span className="text-muted-foreground">${Number(b.split?.tcv || 0).toLocaleString()}</span></div>)}
                {detail.bookings.length === 0 && <span className="text-muted-foreground">None</span>}
              </div>
              <div><div className="font-semibold text-[#111827] mb-1">Consent log ({detail.consent_log.length})</div>
                {detail.consent_log.slice(0, 8).map((c, i) => <div key={i} className="py-1 text-xs text-muted-foreground">{(c.timestamp || "").slice(0, 16).replace("T", " ")} · {c.consent_type} · {c.granted ? "granted" : "denied"}</div>)}
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={() => exportData(detail.id)}><Download size={15} className="mr-1" /> Export</Button>
                {!detail.anonymized && <Button size="sm" variant="outline" className="text-rose-600" onClick={() => anonymize(detail.id)}><Trash2 size={15} className="mr-1" /> Anonymize</Button>}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
